import {find, isEqual, isMatch} from 'lodash';
import {createSelector} from 'reselect';

import type {RootState} from '../../types/redux';
import {deepArrayEqual} from '../../util/compare';
import * as Normalize from './normalize';
import {lookupPart, type StateType} from './normalizerSupport';
import type * as Types from './types';

export const getViews = (state: RootState) => state.views;

export const makeViewListSelector = (
  entityName: string,
  projectName: string,
  viewType: string
) => {
  const params: Types.LoadMetadataListParams = {
    entityName,
    projectName,
    viewType,
  };
  return createSelector(getViews, views => {
    // TODO: Factor this find logic out into a function and call it from
    // reducer
    const list = find(views.lists, q => isMatch(q.query, params));
    if (!list) {
      return {
        loading: false,
        views: [],
      };
    }
    // Perf: I believe it's okay for this to return a new object on every run of the selector
    // here. Since this is in a createSelector, it only gets run when the set of views change.
    // Note that if something else starts generating new views in the selector too frequently,
    // that'll cause issues.
    return {
      loading: list.loading,
      views: list.viewIds.map(vId => views.views[vId]),
    };
  });
};

// Same as above but for a normalized object.
export const makePartSelector = <T extends Types.ObjType>(
  ref: Types.PartRefFromType<T>
) => {
  return (state: RootState) => {
    return lookupPart(state.views.parts, ref) as unknown as NonNullable<
      Types.PartFromType<T>
    >;
  };
};

export const makePartsSelector = <T extends Types.ObjType>(
  refs: Array<Types.PartRefFromType<T>>
) => {
  let result: Array<NonNullable<Types.PartFromType<T>>> = [];
  let lastParts: StateType | null = null;

  const uniqueRefTypes = Array.from(new Set(refs.map(ref => ref.type)));

  return (state: RootState) => {
    if (lastParts != null && state.views.parts === lastParts) {
      return result;
    }

    // Check if the relevant parts of the state haven't changed. If so, return early
    const relevantPartsUnchanged = uniqueRefTypes.every(
      refType =>
        lastParts != null && state.views.parts[refType] === lastParts[refType]
    );
    if (relevantPartsUnchanged) {
      lastParts = state.views.parts;
      return result;
    }

    // Relevant parts have changed, so we need to re-lookup each part
    const newResult = [];
    for (const ref of refs) {
      newResult.push(
        lookupPart(state.views.parts, ref) as unknown as NonNullable<
          Types.PartFromType<T>
        >
      );
    }
    if (!deepArrayEqual(result, newResult)) {
      result = newResult;
    }
    lastParts = state.views.parts;
    return result;
  };
};

export const makeOptionalPartSelector = <T extends Types.ObjType>(
  ref: Types.PartRefFromType<T> | undefined
) => {
  return (state: RootState) => {
    if (ref == null) {
      return undefined;
    }
    return lookupPart(state.views.parts, ref);
  };
};

export const makePartMappedSelector = <T extends Types.ObjType, R>(
  ref: Types.PartRefFromType<T>,
  map: (part: Types.PartFromType<T>) => R,
  stateRef: {prevPart?: Types.PartFromType<T>; result?: R}
) => {
  return (state: RootState) => {
    // TODO(john): shady unknown casting
    const part = makePartSelector(ref)(state) as unknown as
      | Types.PartFromType<T>
      | undefined;

    if (part !== stateRef.prevPart) {
      const mapped = (map as any)(part);
      if (!isEqual(mapped, stateRef.result)) {
        stateRef.result = mapped;
      }
      stateRef.prevPart = part as unknown as typeof part | undefined;
    }
    return stateRef.result!;
  };
};

export const makePartExistsSelector =
  (ref: Types.AllPartRefs | null) => (state: RootState) => {
    if (ref == null) {
      return null;
    }
    return Normalize.partExists(state.views.parts, ref);
  };

export const makeNameSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.name;
  };
};

export const makeModifiedSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return false;
    }
    const view = state.views.views[ref.id];
    return view!.modified;
  };
};

export const makeAutosaveSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return false;
    }
    const view = state.views.views[ref.id];
    return view!.autoSave;
  };
};

export const makeUndoSelector = () => {
  return (state: RootState) => {
    return state.views.undoActions;
  };
};

export const makeRedoSelector = () => {
  return (state: RootState) => {
    return state.views.redoActions;
  };
};

export const makeDescriptionSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return '';
    }
    const view = state.views.views[ref.id];
    return view!.description;
  };
};

export const makeUsernameSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.user.username;
  };
};

export const makeProjectNameSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.project ? view!.project.name : null;
  };
};

export const makeEntityNameSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.project ? view!.project.entityName : null;
  };
};

export const makeExistsServerSelector = <T extends Types.ViewObjSchema>(
  ref: Types.ViewRefFromObjSchema<T> | null
) => {
  return (state: RootState) => {
    if (ref == null) {
      return false;
    }
    const view = state.views.views[ref.id];
    return view!.id != null;
  };
};

export const makeStarCountSelector = (ref: Types.ViewRef | null) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.starCount;
  };
};

export const makeStarredSelector = (ref: Types.ViewRef | null) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.starred;
  };
};

export const makeStarLoadingSelector = (ref: Types.ViewRef | null) => {
  return (state: RootState) => {
    if (ref == null) {
      return null;
    }
    const view = state.views.views[ref.id];
    return view!.starLoading;
  };
};

export const makePanelCommentsEnabledSelector = (ref: Types.ViewRef | null) => {
  return (state: RootState) => {
    if (ref == null) {
      return false;
    }
    const view = state.views.views[ref.id];
    return view!.panelCommentsEnabled;
  };
};

export function makeHasUnsavedChangesSelector(ref: Types.ViewRef) {
  return createSelector(
    (state: RootState) => state.views.views[ref.id].undoIdAtLastSave,
    (state: RootState) =>
      state.views.undoActions[state.views.undoActions.length - 1]?.id,
    (undoIdAtLastSave, currentUndoId) => undoIdAtLastSave !== currentUndoId
  );
}
